import React from "react";
import { PersonFill, CalendarEvent, Clock, GeoAlt } from "../icons";
import { Tag, MessageSquare, Package, Hash, BadgePercent } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { PreOrderItem, TaxBreakdownEntry } from "../../../common/jsonRpcTypes";
import { formatCurrency } from "../../../utils/countryLocaleMap";
import type { BookingTypeGroup } from "../../utils/bookingTypeGroups";
import BookingTypeGroupSummary from "../shared/BookingTypeGroupSummary";
import ItemOptionsDisplay from "../shared/ItemOptionsDisplay";
import { useGuestCheckoutSettings, useUsesDesign } from "../context/GuestCheckoutSettingsContext";

interface BookingDetailsCardProps {
  partySize: number;
  date: string;
  time: string;
  location: string;
  preOrderItems?: PreOrderItem[];
  /** Per-booking-type breakdown, populated only for linked bookings. */
  bookingTypeGroups?: BookingTypeGroup[];
  currency?: string;
  countryCode?: string | null;
  shouldHidePrices?: boolean;
  hidePricesByMenu?: Record<string, boolean>;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  totalCostWithService?: number;
  taxInclusive?: boolean;
  taxBreakdown?: TaxBreakdownEntry[];
  totalNet?: number;
  totalGross?: number;
  taxLabel?: string;
  customerName?: string;
  bookingType?: string;
  specialRequest?: string;
  requestedPackages?: string;
  /** Title of the offer the guest added to the booking, when they chose one. */
  selectedOffer?: string;
  bookingRef?: string;
  depositAmount?: number;
}

interface PreOrderTotalsProps {
  isTaxExclusive: boolean;
  taxBreakdown?: TaxBreakdownEntry[];
  totalNet?: number;
  totalGross?: number;
  label: string;
  itemsSubtotal: number;
  serviceChargeAmount?: number;
  serviceChargePercentage?: number;
  computedTotal: number;
  currency: string;
  countryCode: string;
}

function PreOrderTotals({
  isTaxExclusive,
  taxBreakdown,
  totalNet,
  totalGross,
  label,
  itemsSubtotal,
  serviceChargeAmount,
  serviceChargePercentage,
  computedTotal,
  currency,
  countryCode,
}: Readonly<PreOrderTotalsProps>) {
  const { t } = useTranslation();

  const serviceChargeRow =
    serviceChargeAmount !== undefined && serviceChargeAmount > 0 ? (
      <div className="gcw:flex gcw:justify-between gcw:items-center gcw:mb-2 gcw:mt-bs-3">
        <div className="gcw:text-ref">
          {t("service_charge", "Service Charge")}
          {serviceChargePercentage !== undefined && ` (${serviceChargePercentage}%)`}:
        </div>
        <div className="gcw:text-ref gcw:font-bold">{formatCurrency(serviceChargeAmount, currency, countryCode)}</div>
      </div>
    ) : null;

  if (isTaxExclusive) {
    return (
      <>
        <div className="gcw:flex gcw:justify-between gcw:items-center gcw:mb-2 gcw:mt-bs-3">
          <div className="gcw:text-ref">{t("net_label", "Net")}:</div>
          <div className="gcw:text-ref">{formatCurrency(totalNet ?? itemsSubtotal, currency, countryCode)}</div>
        </div>
        {serviceChargeRow}
        {taxBreakdown!.map((entry) => (
          <div key={entry.label} className="gcw:flex gcw:justify-between gcw:items-center gcw:mb-2">
            <div className="gcw:text-ref">{entry.label}:</div>
            <div className="gcw:text-ref">{formatCurrency(entry.amount, currency, countryCode)}</div>
          </div>
        ))}
        <div className="booking-total gcw:flex gcw:justify-between gcw:items-center gcw:mb-2">
          <div className="gcw:text-ref">{t("tax_total_label", "Total")}:</div>
          <div className="gcw:text-ref">{formatCurrency(totalGross ?? computedTotal, currency, countryCode)}</div>
        </div>
      </>
    );
  }

  return (
    <>
      {serviceChargeRow}
      {computedTotal > 0 && (
        <div className="booking-total gcw:flex gcw:justify-between gcw:items-center gcw:mb-2">
          <div className="gcw:text-ref">
            {t("total", "Total")} ({t("inc_vat", "inc {{taxLabel}}", { taxLabel: label })}):
          </div>
          <div className="gcw:text-ref">{formatCurrency(computedTotal, currency, countryCode)}</div>
        </div>
      )}
    </>
  );
}

interface PreOrderItemLineProps {
  item: PreOrderItem;
  hidePrice: boolean;
  currency: string;
  countryCode: string;
}

/** One pre-ordered item: its name, quantity, the guest it is for and the options chosen for it. */
function PreOrderItemLine({ item, hidePrice, currency, countryCode }: Readonly<PreOrderItemLineProps>) {
  const { t } = useTranslation();

  return (
    <div className="gcw:flex gcw:justify-between gcw:items-center gcw:mb-2">
      <div>
        <span className="gcw:text-ref">{item.name}</span>
        {item.quantity > 1 && <span className="gcw:text-ref"> × {item.quantity}</span>}
        {item.for_name && (
          <small className="gcw:text-ref">
            {"\u00A0"}({t("for", "for")} {item.for_name})
          </small>
        )}
        <ItemOptionsDisplay
          options={item.options}
          currency={currency}
          countryCode={countryCode}
          hidePrices={hidePrice}
          className="item-options"
          labelClassName="item-options-label"
          valueClassName="item-options-value"
        />
      </div>
      {!hidePrice && <div className="gcw:text-ref">{formatCurrency(item.total ?? 0, currency, countryCode)}</div>}
    </div>
  );
}

/**
 * A pre-ordered entry, which is either a single item or a fixed-price menu.
 *
 * A fixed-price menu is stored as a container whose nested items hold what the
 * guest actually chose, so those are listed beneath the menu line — otherwise
 * only the menu name reaches the guest and their configurable options are lost.
 * The container carries the price set by the menu's fixed-price rule, so the
 * nested items are listed without prices of their own.
 */
function PreOrderItemEntry({ item, hidePrice, currency, countryCode }: Readonly<PreOrderItemLineProps>) {
  const nestedItems = item.type === "menu" && Array.isArray(item.items) ? item.items : [];

  if (nestedItems.length === 0) {
    return <PreOrderItemLine item={item} hidePrice={hidePrice} currency={currency} countryCode={countryCode} />;
  }

  return (
    <div>
      <PreOrderItemLine item={item} hidePrice={hidePrice} currency={currency} countryCode={countryCode} />
      <ul className="gcw:list-none gcw:ps-bs-3 gcw:m-0 fixed-price-menu-items">
        {nestedItems.map((nestedItem, nestedIdx) => (
          <li key={`${nestedItem.id}-${nestedIdx}`}>
            <PreOrderItemLine item={nestedItem} hidePrice currency={currency} countryCode={countryCode} />
          </li>
        ))}
      </ul>
    </div>
  );
}

interface BookingDetailRowsProps {
  partySize: number;
  date: string;
  time: string;
  location: string;
  customerName?: string;
  bookingType?: string;
  bookingRef?: string;
  specialRequest?: string;
  requestedPackages?: string;
  selectedOffer?: string;
}

/** The app's two-column grids of stacked label/value cells, icons included. */
function AppBookingDetailRows({
  partySize,
  date,
  time,
  location,
  customerName,
  bookingType,
  bookingRef,
  specialRequest,
  requestedPackages,
  selectedOffer,
}: Readonly<BookingDetailRowsProps>) {
  const { t } = useTranslation();

  return (
    <div className="gcw:space-y-4">
      <div className="gcw:grid gcw:grid-cols-1 gcw:sm:grid-cols-2 gcw:gap-4">
        {partySize > 0 && (
          <div className="gcw:w-full booking-detail-col">
            <div className="detail-item">
              <div className="detail-label gcw:text-text-muted">{t("booking_details_party_size", "Party Size")}</div>
              <div className="detail-value gcw:text-order-details">
                <PersonFill className="gcw:me-2" />
                {partySize}{" "}
                {partySize === 1 ? t("booking_details_guest", "guest") : t("booking_details_guests", "guests")}
              </div>
            </div>
          </div>
        )}
        <div className="gcw:w-full booking-detail-col">
          <div className="detail-item">
            <div className="detail-label gcw:text-text-muted">{t("booking_details_date", "Date")}</div>
            <div className="detail-value gcw:text-order-details">
              <CalendarEvent className="gcw:me-2" />
              {date}
            </div>
          </div>
        </div>
      </div>

      <div className="gcw:grid gcw:grid-cols-1 gcw:sm:grid-cols-2 gcw:gap-4">
        {time && (
          <div className="gcw:w-full booking-detail-col">
            <div className="detail-item">
              <div className="detail-label gcw:text-text-muted">{t("booking_details_time", "Time")}</div>
              <div className="detail-value gcw:text-order-details">
                <Clock className="gcw:me-2" />
                {time}
              </div>
            </div>
          </div>
        )}
        <div className="gcw:w-full booking-detail-col">
          <div className="detail-item">
            <div className="detail-label gcw:text-text-muted">{t("booking_details_location", "Location")}</div>
            <div className="detail-value gcw:text-order-details">
              <GeoAlt className="gcw:me-2" />
              {location}
            </div>
          </div>
        </div>
      </div>

      {(customerName || bookingType) && (
        <div className="gcw:grid gcw:grid-cols-1 gcw:sm:grid-cols-2 gcw:gap-4">
          {customerName && (
            <div className="gcw:w-full booking-detail-col">
              <div className="detail-item">
                <div className="detail-label gcw:text-text-muted">{t("booking_detail_customer_name", "Name")}</div>
                <div className="detail-value gcw:text-order-details">
                  <PersonFill className="gcw:me-2" />
                  {customerName}
                </div>
              </div>
            </div>
          )}
          {bookingType && (
            <div className="gcw:w-full booking-detail-col">
              <div className="detail-item">
                <div className="detail-label gcw:text-text-muted">{t("booking_type", "Booking Type")}</div>
                <div className="detail-value gcw:text-order-details">
                  <Tag className="gcw:me-2" size={16} />
                  {bookingType}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {bookingRef && (
        <div className="gcw:grid gcw:grid-cols-1 gcw:sm:grid-cols-2 gcw:gap-4">
          <div className="gcw:w-full booking-detail-col">
            <div className="detail-item">
              <div className="detail-label gcw:text-text-muted">{t("booking_reference", "Booking reference")}</div>
              <div className="detail-value gcw:text-order-details">
                <Hash className="gcw:me-2" size={16} />
                {bookingRef}
              </div>
            </div>
          </div>
        </div>
      )}

      {specialRequest && (
        <div className="gcw:grid gcw:grid-cols-1 gcw:gap-4">
          <div className="gcw:w-full booking-detail-col">
            <div className="detail-item">
              <div className="detail-label gcw:text-text-muted">
                {t("booking_detail_special_request", "Special Request")}
              </div>
              <div className="detail-value gcw:text-order-details">
                <MessageSquare className="gcw:me-2" size={16} />
                {specialRequest}
              </div>
            </div>
          </div>
        </div>
      )}

      {requestedPackages && (
        <div className="gcw:grid gcw:grid-cols-1 gcw:gap-4">
          <div className="gcw:w-full booking-detail-col">
            <div className="detail-item">
              <div className="detail-label gcw:text-text-muted">
                {t("booking_detail_requested_packages", "Requested Packages")}
              </div>
              <div className="detail-value gcw:text-order-details">
                <Package className="gcw:me-2" size={16} />
                {requestedPackages}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedOffer && (
        <div className="gcw:grid gcw:grid-cols-1 gcw:gap-4">
          <div className="gcw:w-full booking-detail-col">
            <div className="detail-item">
              <div className="detail-label gcw:text-text-muted">
                {t("booking_detail_selected_offer", "Selected Offer")}
              </div>
              <div className="detail-value gcw:text-order-details">
                <BadgePercent className="gcw:me-2" size={16} />
                {selectedOffer}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface InlineBookingDetailRow {
  key: string;
  label: string;
  value: React.ReactNode;
}

/** A row the design may carry: kept only when it has a value, unless the booking always has one. */
interface InlineBookingDetailRowCandidate extends InlineBookingDetailRow {
  alwaysShown?: boolean;
}

/**
 * The design lays the booking details out as inline `Label: value` rows with
 * no icons, rather than the app's two-column grid of stacked label/value
 * cells carrying one each.
 *
 * The widget keeps them in a single column: it is a narrow surface, and that
 * is the layout its design was signed off on and its e2e audit measures.
 *
 * The wider surfaces flow them into two columns once there is room, so a dozen
 * rows do not read as one long ribbon down the left of a wide card. "Room" is
 * measured on the card, not the viewport: an embedded element takes whatever
 * width its host gives it, so a `lg:` breakpoint would split a 320px sidebar
 * into two columns on a 1440px screen. A container query asks the question
 * that actually matters. Row-major order, so the reading order matches the
 * DOM.
 */
function InlineBookingDetailRows({
  rows,
  columns,
}: Readonly<{ rows: InlineBookingDetailRow[]; columns: "single" | "two-when-wide" }>) {
  const isSingle = columns === "single";
  const layout = isSingle
    ? "gcw:flex gcw:flex-col gcw:gap-2"
    : "gcw:grid gcw:grid-cols-1 gcw:gap-2 gcw:@min-[34rem]:grid-cols-2 gcw:@min-[34rem]:gap-x-6";

  const list = (
    <div className={layout}>
      {rows.map((row) => (
        <div className="detail-item gcw:flex gcw:flex-wrap gcw:items-baseline gcw:gap-2" key={row.key}>
          <span className="detail-label gcw:mb-0 gcw:text-text-muted">{row.label}:</span>
          <span className="detail-value gcw:text-order-details">{row.value}</span>
        </div>
      ))}
    </div>
  );

  // The query container the columns are measured against. Only established
  // where it is read, so the widget's single column is not put under
  // containment it has no use for.
  return isSingle ? list : <div className="gcw:@container">{list}</div>;
}

/** The design's booking details: inline rows, in the design's reading order. */
function DesignBookingDetailRows({
  partySize,
  date,
  time,
  location,
  customerName,
  bookingType,
  bookingRef,
  specialRequest,
  requestedPackages,
  selectedOffer,
  columns,
}: Readonly<BookingDetailRowsProps & { columns: "single" | "two-when-wide" }>) {
  const { t } = useTranslation();

  const partySizeText = `${partySize} ${
    partySize === 1 ? t("booking_details_guest", "guest") : t("booking_details_guests", "guests")
  }`;

  const candidates: InlineBookingDetailRowCandidate[] = [
    { key: "bookingRef", label: t("widget_booking_reference", "Booking Reference"), value: bookingRef },
    { key: "customerName", label: t("booking_detail_customer_name", "Name"), value: customerName },
    {
      key: "partySize",
      label: t("booking_details_party_size", "Party Size"),
      value: partySize > 0 ? partySizeText : "",
    },
    { key: "location", label: t("booking_details_location", "Location"), value: location, alwaysShown: true },
    { key: "date", label: t("booking_details_date", "Date"), value: date, alwaysShown: true },
    { key: "time", label: t("booking_details_time", "Time"), value: time },
    { key: "bookingType", label: t("booking_type", "Booking Type"), value: bookingType },
    { key: "specialRequest", label: t("booking_detail_special_request", "Special Request"), value: specialRequest },
    {
      key: "requestedPackages",
      label: t("booking_detail_requested_packages", "Requested Packages"),
      value: requestedPackages,
    },
    { key: "selectedOffer", label: t("booking_detail_selected_offer", "Selected Offer"), value: selectedOffer },
  ];

  const rows = candidates.filter((row) => row.alwaysShown === true || Boolean(row.value));

  return <InlineBookingDetailRows rows={rows} columns={columns} />;
}

const BookingDetailsCard: React.FC<BookingDetailsCardProps> = ({
  partySize,
  date,
  time,
  location,
  preOrderItems,
  bookingTypeGroups,
  currency,
  countryCode,
  shouldHidePrices,
  hidePricesByMenu,
  serviceChargeAmount,
  serviceChargePercentage,
  totalCostWithService,
  taxInclusive,
  taxBreakdown,
  totalNet,
  totalGross,
  taxLabel,
  customerName,
  bookingType,
  specialRequest,
  requestedPackages,
  selectedOffer,
  bookingRef,
  depositAmount,
}) => {
  const { t } = useTranslation();
  const { isWidget } = useGuestCheckoutSettings();
  const usesDesign = useUsesDesign();
  const itemsArray = preOrderItems ?? [];
  const hasPreOrderItems = itemsArray.length > 0;
  const hasAnyHiddenPrice = itemsArray.some((item) => hidePricesByMenu?.[item.menu_id]);

  const isTaxExclusive = taxInclusive === false && Array.isArray(taxBreakdown) && taxBreakdown.length > 0;
  const label = taxLabel ?? "VAT";
  const depositPaid = depositAmount !== undefined && depositAmount > 0 ? depositAmount : null;

  const itemsSubtotal = itemsArray.reduce((sum, item) => sum + (item.total ?? 0), 0);
  const computedTotal = totalCostWithService ?? itemsSubtotal + (serviceChargeAmount ?? 0);
  // A linked booking spans several booking types, so show the items grouped by
  // booking type instead of one merged list. The totals below stay combined.
  const groups = bookingTypeGroups ?? [];

  return (
    <div className="gcw:rounded-lg gcw:border gcw:border-gray-200 gcw:bg-white gcw:shadow-sm booking-details-card gcw:mb-bs-4">
      <div className="gcw:p-bs-3">
        <h2 className="gcw:text-subtitle gcw:mb-2">{t("booking_details_title", "Your Booking Details")}</h2>

        {usesDesign ? (
          <DesignBookingDetailRows
            partySize={partySize}
            date={date}
            time={time}
            location={location}
            customerName={customerName}
            bookingType={bookingType}
            bookingRef={bookingRef}
            specialRequest={specialRequest}
            requestedPackages={requestedPackages}
            selectedOffer={selectedOffer}
            columns={isWidget ? "single" : "two-when-wide"}
          />
        ) : (
          <AppBookingDetailRows
            partySize={partySize}
            date={date}
            time={time}
            location={location}
            customerName={customerName}
            bookingType={bookingType}
            bookingRef={bookingRef}
            specialRequest={specialRequest}
            requestedPackages={requestedPackages}
            selectedOffer={selectedOffer}
          />
        )}

        {hasPreOrderItems && (
          <>
            <h3 className="gcw:mt-[10px] gcw:py-bs-3 gcw:font-bold gcw:border-t gcw:border-light-grey">
              {t("booking_details_preorder_title", "Pre-ordered Items")}
            </h3>
            {groups.length > 0 ? (
              <BookingTypeGroupSummary
                groups={groups}
                currency={currency ?? "GBP"}
                countryCode={countryCode ?? "GB"}
                hidePricesByMenu={hidePricesByMenu}
                shouldHidePrices={shouldHidePrices}
                headingLevel="h4"
                className="pre-order-items"
              />
            ) : (
              <div className="pre-order-items">
                {itemsArray.map((item) => {
                  const itemHidePrices = (hidePricesByMenu?.[item.menu_id] ?? false) || (shouldHidePrices ?? false);
                  return (
                    <PreOrderItemEntry
                      key={`${item.id}-${item.for_name ?? ""}`}
                      item={item}
                      hidePrice={itemHidePrices}
                      currency={currency ?? "GBP"}
                      countryCode={countryCode ?? "GB"}
                    />
                  );
                })}
              </div>
            )}
            {!hasAnyHiddenPrice && (
              <PreOrderTotals
                isTaxExclusive={isTaxExclusive}
                taxBreakdown={taxBreakdown}
                totalNet={totalNet}
                totalGross={totalGross}
                label={label}
                itemsSubtotal={itemsSubtotal}
                serviceChargeAmount={serviceChargeAmount}
                serviceChargePercentage={serviceChargePercentage}
                computedTotal={computedTotal}
                currency={currency ?? "GBP"}
                countryCode={countryCode ?? "GB"}
              />
            )}
          </>
        )}

        {/* A paid deposit is charged for the booking, not for a menu, so it is
            shown whether or not the guest pre-ordered. It takes the separator of
            the pre-order block when that block is absent. */}
        {depositPaid !== null && !hasAnyHiddenPrice && (
          <div
            className={`gcw:flex gcw:justify-between gcw:items-center gcw:mb-2 ${
              hasPreOrderItems ? "" : "gcw:border-t gcw:border-light-grey gcw:pt-bs-3 gcw:mt-bs-3"
            }`}
          >
            <div className="gcw:text-ref gcw:font-bold!">{t("booking_deposit_amount", "Deposit")}:</div>
            <div className="gcw:text-ref gcw:font-bold!">
              {formatCurrency(depositPaid, currency ?? "GBP", countryCode ?? "GB")}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingDetailsCard;
